import { useMemo, VFC } from 'react';

import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import {
  caseAttachmentsFor,
  filterUsableObjects,
  UsableObjectConfig,
} from 'dashboard/lib/workflows';
import { accountAttachmentsFor, TemplateFilter } from 'dashboard/lib/workflows/objects';
import { WorkflowError, WorkflowFieldObjectKey } from 'dashboard/models/workflow';
import { WorkflowActionAttachObjectsV1 } from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';

interface ParamsAttachObjectsV1Props extends ParamsBaseProps {
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionAttachObjectsV1['params'];
  usableObjects: UsableObjectConfig[];
  updateActionParams: (
    newParams: WorkflowActionAttachObjectsV1['params'],
    template?: TemplateFilter,
  ) => void;
}

const ParamsAttachObjectsV1: VFC<ParamsAttachObjectsV1Props> = ({
  editable,
  errors,
  params,
  usableObjects,
  updateActionParams,
}) => {
  const isAccountObjectAttachmentEnabled = useFeatureFlag(
    'dashboard:accounts-enable-account-object-attachment-in-workflows',
  );

  const possibleTargetObjects = useMemo(() => {
    const permittedObjects = isAccountObjectAttachmentEnabled
      ? [WorkflowFieldObjectKey.Account, WorkflowFieldObjectKey.Case, WorkflowFieldObjectKey.SAR]
      : [WorkflowFieldObjectKey.Case, WorkflowFieldObjectKey.SAR];

    return filterUsableObjects({
      usableObjects,
      options: {
        permittedObjects: permittedObjects,
        excludeNonPersonaObjects: true,
      },
    });
  }, [usableObjects]);

  const selectTargetObject = (value: string) => {
    updateActionParams({
      ...params,
      target: value,
    });
  };

  const selectObjectsToAttach = (values: string[]) => {
    updateActionParams({
      ...params,
      objects: values,
    });
  };

  const possibleAttachableObjects = useMemo(() => {
    const targetObjectType = usableObjects.find(
      (uo) => uo.value === params['target'],
    )?.workflowObjectKey;

    switch (targetObjectType) {
      case WorkflowFieldObjectKey.Account:
        return accountAttachmentsFor(usableObjects);
      case WorkflowFieldObjectKey.SAR:
        return filterUsableObjects({
          usableObjects,
          options: {
            permittedObjects: [WorkflowFieldObjectKey.Account, WorkflowFieldObjectKey.Transaction],
            excludeNonPersonaObjects: true,
          },
        });
      case WorkflowFieldObjectKey.Case:
        return caseAttachmentsFor(usableObjects);
    }
  }, [usableObjects, params['target']]);

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionAttachObjectsV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel labelText='Target Object' htmlFor='params-attach-objects__target-object-input' />
      <ReactSelect
        value={params['target']}
        options={possibleTargetObjects}
        onChange={selectTargetObject}
        id='params-attach-objects__target-object'
        disabled={!editable}
        hasError={hasErrorFor('target')}
      />
      <Spacer height={2} />
      <ParamLabel
        labelText='Objects to attach'
        htmlFor='params-attach-objects__objects-to-attach-input'
      />
      <ReactSelect
        value={params['objects']}
        isMulti
        options={possibleAttachableObjects}
        onChange={selectObjectsToAttach}
        id='params-attach-objects__objects-to-attach'
        disabled={!editable}
        hasError={hasErrorFor('objects')}
      />
      {renderErrorMessageFor('objects')}
    </>
  );
};

export default ParamsAttachObjectsV1;
